import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboardData } from "../../services/dashboard";
import "./dashboard.css";

function formatValue(item) {
  if (item?.type === "currency") {
    const amount = Number(item?.value || 0);
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: item?.currency || "NGN",
      maximumFractionDigits: 0,
    }).format(amount);
  }

  return new Intl.NumberFormat("en-NG").format(Number(item?.value || 0));
}

export default function Dashboard() {
  const navigate = useNavigate();

  const [data, setData] = useState({
    hero: {
      eyebrow: "Operational Overview",
      title: "Payroll operations at a glance",
      subtitle:
        "Track payroll status, approvals, compliance, and key workforce metrics from a single workspace.",
      chips: [],
    },
    stats: [],
    alerts: [],
    quick_links: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const heroChips = useMemo(() => data?.hero?.chips || [], [data]);

  useEffect(() => {
    let active = true;

    async function loadDashboard() {
      setLoading(true);
      setError("");

      try {
        const response = await getDashboardData();
        if (active) {
          setData(response);
        }
      } catch (err) {
        if (active) {
          setError(err?.response?.data?.message || "Failed to load dashboard data.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadDashboard();

    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="dashboard-page">
      <section className="dashboard-hero payroll-card">
        <div className="dashboard-hero__content">
          <span className="dashboard-hero__eyebrow">{data.hero.eyebrow}</span>
          <h2>{data.hero.title}</h2>
          <p>{data.hero.subtitle}</p>
        </div>

        <div className="dashboard-hero__chip-group">
          {heroChips.map((chip, index) => (
            <span
              key={`${chip.label}-${index}`}
              className={`dashboard-chip dashboard-chip--${chip.tone || "neutral"}`}
            >
              {chip.label}
            </span>
          ))}
        </div>
      </section>

      {error ? <div className="dashboard-error payroll-card">{error}</div> : null}

      <section className="dashboard-stats">
        {(data.stats || []).map((item) => (
          <article key={item.key || item.label} className="dashboard-stat payroll-card">
            <span className="dashboard-stat__label">{item.label}</span>
            <strong className="dashboard-stat__value">{formatValue(item)}</strong>
            <p className="dashboard-stat__note">{item.note}</p>
          </article>
        ))}
      </section>

      <section className="dashboard-grid">
        <article className="payroll-card dashboard-panel">
          <div className="dashboard-panel__header">
            <div>
              <span className="dashboard-panel__eyebrow">Attention Needed</span>
              <h3>Workflow Alerts</h3>
            </div>
          </div>

          <div className="dashboard-alerts">
            {loading ? (
              <div className="dashboard-alert">
                <div className="dashboard-alert__dot" />
                <span>Loading alerts...</span>
              </div>
            ) : (
              (data.alerts || []).map((item, index) => (
                <div key={`${item.message}-${index}`} className="dashboard-alert">
                  <div className="dashboard-alert__dot" />
                  <span>{item.message}</span>
                </div>
              ))
            )}
          </div>
        </article>

        <article className="payroll-card dashboard-panel">
          <div className="dashboard-panel__header">
            <div>
              <span className="dashboard-panel__eyebrow">Quick Actions</span>
              <h3>Common Tasks</h3>
            </div>
          </div>

          <div className="dashboard-actions">
            {(data.quick_links || []).map((item) => (
              <button
                key={item.title}
                type="button"
                className="dashboard-action"
                onClick={() => item.path && navigate(item.path)}
              >
                <strong>{item.title}</strong>
                <span>{item.subtitle}</span>
              </button>
            ))}
          </div>
        </article>
      </section>
    </div>
  );
}
